import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  RefreshCw,
  Loader2,
  DollarSign,
  Users,
  Sparkles,
  Download,
  Filter,
  TrendingUp,
  Tag,
  ListChecks
} from 'lucide-react';
import { contractService } from '../../services/contractService';
import { Contract } from '../../types/contract';
import { PageTransition } from '../ui/PageTransition';
import { PageTitle } from '../ui/PageTitle';
import { formatCurrency, parseMoneyValue } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateUtils';
import { normalizeLabel, titleCase, splitCharacterNames } from '../../utils/textUtils';

// --- Parsing helpers -------------------------------------------------

const parseMoney = parseMoneyValue;
const splitCharacters = splitCharacterNames;

const round5 = (value: number): number => Math.round(value / 5) * 5;

const median = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const avg = (values: number[]): number => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0);

interface StatRow {
  key: string;
  label: string;
  count: number;
  min: number;
  max: number;
  avgValue: number;
  suggested: number;
  extra?: string;
}

const GUEST_BRACKETS: Array<{ label: string; min: number; max: number }> = [
  { label: 'até 20', min: 0, max: 20 },
  { label: '21 - 40', min: 21, max: 40 },
  { label: '41 - 60', min: 41, max: 60 },
  { label: '61 - 100', min: 61, max: 100 },
  { label: '101+', min: 101, max: Infinity }
];

const downloadCSV = (filename: string, rows: string[][]) => {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// --- Mini bar chart (no external deps) --------------------------------

const MiniBarChart: React.FC<{
  data: Array<{ label: string; value: number; count: number }>;
  valueFormatter?: (n: number) => string;
}> = ({ data, valueFormatter = (n) => formatCurrency(n) }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3 text-sm">
          <div className="w-32 shrink-0 truncate text-slate-600" title={d.label}>{d.label}</div>
          <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-400 to-teal-600 rounded-full flex items-center justify-end px-2 text-[10px] text-white font-semibold"
              style={{ width: `${Math.max((d.value / max) * 100, 6)}%` }}
            >
              {valueFormatter(d.value)}
            </div>
          </div>
          <div className="w-16 shrink-0 text-right text-xs text-slate-400">{d.count}x</div>
        </div>
      ))}
      {data.length === 0 && <p className="text-sm text-slate-400">Sem dados suficientes.</p>}
    </div>
  );
};

const StatTable: React.FC<{ title: string; icon: React.ReactNode; rows: StatRow[]; onExport?: () => void }> = ({
  title,
  icon,
  rows,
  onExport
}) => (
  <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
      <div className="flex items-center gap-2 font-semibold text-slate-800">
        {icon}
        {title}
      </div>
      {onExport && rows.length > 0 && (
        <button
          onClick={onExport}
          className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900"
        >
          <Download className="w-3.5 h-3.5" />
          Exportar CSV
        </button>
      )}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            <th className="text-left px-5 py-2 font-semibold">Item</th>
            <th className="text-right px-3 py-2 font-semibold">Ocorrências</th>
            <th className="text-right px-3 py-2 font-semibold">Mínimo</th>
            <th className="text-right px-3 py-2 font-semibold">Médio</th>
            <th className="text-right px-3 py-2 font-semibold">Máximo</th>
            <th className="text-right px-5 py-2 font-semibold text-teal-700">Sugerido</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.key} className="hover:bg-slate-50">
              <td className="px-5 py-2.5 font-medium text-slate-800">
                {row.label}
                {row.extra && <span className="block text-xs font-normal text-slate-400">{row.extra}</span>}
              </td>
              <td className="px-3 py-2.5 text-right text-slate-500">{row.count}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{formatCurrency(row.min)}</td>
              <td className="px-3 py-2.5 text-right text-slate-700">{formatCurrency(row.avgValue)}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{formatCurrency(row.max)}</td>
              <td className="px-5 py-2.5 text-right font-bold text-teal-700">{formatCurrency(row.suggested)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-5 py-6 text-center text-slate-400">
                Nenhum dado encontrado com os filtros atuais.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </section>
);

const StatCard: React.FC<{ label: string; value: string; hint?: string; icon: React.ReactNode }> = ({
  label,
  value,
  hint,
  icon
}) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase mb-2">
      {icon}
      {label}
    </div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </div>
);

// --- Main component -----------------------------------------------------

const BudgetInsights: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [themeFilter, setThemeFilter] = useState('todos');
  const [minGuests, setMinGuests] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [onlyWithComponents, setOnlyWithComponents] = useState(false);
  const [sortKey, setSortKey] = useState<'data' | 'valor' | 'convidados'>('data');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await contractService.getContracts();
      setContracts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar contratos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Only events that actually have a defined total price - these are the
  // ones that carry real market signal for building a price table.
  const pricedContracts = useMemo(() => {
    return contracts
      .map((c) => ({ contract: c, total: parseMoney(c.valor) }))
      .filter((entry) => !isNaN(entry.total) && entry.total > 0);
  }, [contracts]);

  const themes = useMemo(() => {
    const set = new Set<string>();
    pricedContracts.forEach(({ contract }) => {
      const t = normalizeLabel(contract.temaFesta || '');
      if (t) set.add(t);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [pricedContracts]);

  const filtered = useMemo(() => {
    const min = minGuests ? parseInt(minGuests, 10) : null;
    const max = maxGuests ? parseInt(maxGuests, 10) : null;
    return pricedContracts.filter(({ contract }) => {
      if (themeFilter !== 'todos' && normalizeLabel(contract.temaFesta || '') !== themeFilter) return false;
      const guests = Number(contract.numeroConvidados) || 0;
      if (min !== null && guests < min) return false;
      if (max !== null && guests > max) return false;
      if (onlyWithComponents) {
        const hasComponents =
          !isNaN(parseMoney(contract.brinquedosPrice)) ||
          !isNaN(parseMoney(contract.recreacaoPrice)) ||
          !isNaN(parseMoney(contract.personagensPrice)) ||
          (contract.customBudgetFields && contract.customBudgetFields.length > 0);
        if (!hasComponents) return false;
      }
      return true;
    });
  }, [pricedContracts, themeFilter, minGuests, maxGuests, onlyWithComponents]);

  const summary = useMemo(() => {
    const totals = filtered.map((f) => f.total);
    const guestsList = filtered
      .map((f) => Number(f.contract.numeroConvidados) || 0)
      .filter((g) => g > 0);
    const perGuest = filtered
      .map((f) => {
        const g = Number(f.contract.numeroConvidados) || 0;
        return g > 0 ? f.total / g : null;
      })
      .filter((v): v is number => v !== null);

    return {
      count: filtered.length,
      avgTotal: avg(totals),
      medianTotal: median(totals),
      minTotal: totals.length ? Math.min(...totals) : 0,
      maxTotal: totals.length ? Math.max(...totals) : 0,
      avgGuests: avg(guestsList),
      avgPricePerGuest: avg(perGuest)
    };
  }, [filtered]);

  const themeRows = useMemo<StatRow[]>(() => {
    const groups = new Map<string, number[]>();
    filtered.forEach(({ contract, total }) => {
      const key = normalizeLabel(contract.temaFesta || '') || 'Sem tema informado';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(total);
    });
    return Array.from(groups.entries())
      .map(([label, values]) => ({
        key: label,
        label,
        count: values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        avgValue: avg(values),
        suggested: round5(avg(values))
      }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const guestBracketRows = useMemo<StatRow[]>(() => {
    return GUEST_BRACKETS.map((bracket) => {
      const values = filtered
        .filter(({ contract }) => {
          const g = Number(contract.numeroConvidados) || 0;
          return g >= bracket.min && g <= bracket.max;
        })
        .map(({ total }) => total);
      return {
        key: bracket.label,
        label: `${bracket.label} convidados`,
        count: values.length,
        min: values.length ? Math.min(...values) : 0,
        max: values.length ? Math.max(...values) : 0,
        avgValue: avg(values),
        suggested: round5(avg(values))
      };
    }).filter((row) => row.count > 0);
  }, [filtered]);

  const componentRows = useMemo<StatRow[]>(() => {
    const components: Array<{ key: string; label: string; field: keyof Contract }> = [
      { key: 'brinquedos', label: 'Brinquedos', field: 'brinquedosPrice' },
      { key: 'recreacao', label: 'Recreação', field: 'recreacaoPrice' },
      { key: 'personagens', label: 'Personagens (pacote)', field: 'personagensPrice' }
    ];

    return components
      .map(({ key, label, field }) => {
        const values = filtered
          .map(({ contract }) => parseMoney(contract[field] as string | undefined))
          .filter((v) => !isNaN(v) && v > 0);
        return {
          key,
          label,
          count: values.length,
          min: values.length ? Math.min(...values) : 0,
          max: values.length ? Math.max(...values) : 0,
          avgValue: avg(values),
          suggested: round5(avg(values))
        };
      })
      .filter((row) => row.count > 0);
  }, [filtered]);

  const customFieldRows = useMemo<StatRow[]>(() => {
    const groups = new Map<string, { values: number[]; displayLabel: string }>();
    filtered.forEach(({ contract }) => {
      (contract.customBudgetFields || []).forEach((field) => {
        const value = parseMoney(field.value);
        const rawLabel = normalizeLabel(field.label || '');
        if (!rawLabel || isNaN(value) || value <= 0) return;
        const normalizedKey = rawLabel.toLowerCase();
        if (!groups.has(normalizedKey)) {
          groups.set(normalizedKey, { values: [], displayLabel: titleCase(rawLabel) });
        }
        groups.get(normalizedKey)!.values.push(value);
      });
    });
    return Array.from(groups.entries())
      .map(([key, { values, displayLabel }]) => ({
        key,
        label: displayLabel,
        count: values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        avgValue: avg(values),
        suggested: round5(avg(values))
      }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const characterRows = useMemo<StatRow[]>(() => {
    // Estimate a per-character price when both the list and the package
    // price for "personagens" are informed on the same contract.
    const groups = new Map<string, number[]>();
    const frequency = new Map<string, number>();

    filtered.forEach(({ contract }) => {
      const names = splitCharacters(contract.personagensList);
      if (names.length === 0) return;
      names.forEach((name) => {
        const key = name.toLowerCase();
        frequency.set(key, (frequency.get(key) || 0) + 1);
      });
      const packagePrice = parseMoney(contract.personagensPrice);
      if (!isNaN(packagePrice) && packagePrice > 0) {
        const perCharacter = packagePrice / names.length;
        names.forEach((name) => {
          const key = name.toLowerCase();
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key)!.push(perCharacter);
        });
      }
    });

    return Array.from(frequency.entries())
      .map(([key, count]) => {
        const values = groups.get(key) || [];
        return {
          key,
          label: titleCase(key),
          count,
          min: values.length ? Math.min(...values) : 0,
          max: values.length ? Math.max(...values) : 0,
          avgValue: avg(values),
          suggested: round5(avg(values)),
          extra: values.length === 0 ? 'Sem preço individual identificado' : `${values.length} evento(s) com preço estimado`
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const themeChartData = useMemo(
    () => themeRows.slice(0, 8).map((r) => ({ label: r.label, value: r.avgValue, count: r.count })),
    [themeRows]
  );

  const bracketChartData = useMemo(
    () => guestBracketRows.map((r) => ({ label: r.label, value: r.avgValue, count: r.count })),
    [guestBracketRows]
  );

  const sortedTable = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let diff = 0;
      if (sortKey === 'valor') diff = a.total - b.total;
      else if (sortKey === 'convidados') diff = (Number(a.contract.numeroConvidados) || 0) - (Number(b.contract.numeroConvidados) || 0);
      else diff = new Date(a.contract.dataFesta || 0).getTime() - new Date(b.contract.dataFesta || 0).getTime();
      return sortDir === 'asc' ? diff : -diff;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: 'data' | 'valor' | 'convidados') => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const exportSuggestedTable = () => {
    const rows: string[][] = [['Categoria', 'Item', 'Ocorrências', 'Mínimo', 'Médio', 'Máximo', 'Preço Sugerido']];
    const pushRows = (category: string, list: StatRow[]) => {
      list.forEach((row) => {
        rows.push([
          category,
          row.label,
          String(row.count),
          row.min.toFixed(2).replace('.', ','),
          row.avgValue.toFixed(2).replace('.', ','),
          row.max.toFixed(2).replace('.', ','),
          row.suggested.toFixed(2).replace('.', ',')
        ]);
      });
    };
    pushRows('Item do orçamento', componentRows);
    pushRows('Extra / campo personalizado', customFieldRows);
    pushRows('Personagem', characterRows);
    pushRows('Tema (valor total do evento)', themeRows);
    pushRows('Faixa de convidados (valor total)', guestBracketRows);
    downloadCSV('sugestao-tabela-precos-playkids.csv', rows);
  };

  const exportRawData = () => {
    const rows: string[][] = [
      ['Data da Festa', 'Contratante', 'Tema', 'Convidados', 'Valor Total', 'Brinquedos', 'Recreação', 'Personagens', 'Lista de Personagens']
    ];
    sortedTable.forEach(({ contract, total }) => {
      rows.push([
        contract.dataFesta ? formatDate(contract.dataFesta) : '',
        contract.contratante || '',
        contract.temaFesta || '',
        String(contract.numeroConvidados || ''),
        total.toFixed(2).replace('.', ','),
        contract.brinquedosPrice || '',
        contract.recreacaoPrice || '',
        contract.personagensPrice || '',
        contract.personagensList || ''
      ]);
    });
    downloadCSV('eventos-precificados-playkids.csv', rows);
  };

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 space-y-6">
        <div className="mx-2 sm:mx-4 lg:mx-6 mt-2">
          <PageTitle
            title="Estudo de Orçamentos"
            subtitle="Análise dos preços já praticados para ajudar a montar a tabela oficial de preços"
          >
            <button
              onClick={load}
              disabled={loading}
              className="px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30 transition-all duration-200 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Atualizar dados
            </button>
          </PageTitle>
        </div>

        {error && (
          <div className="mx-2 sm:mx-4 lg:mx-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="mx-2 sm:mx-4 lg:mx-6 space-y-6">
            {pricedContracts.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
                Nenhum evento com valor definido foi encontrado ainda. Assim que orçamentos forem
                preenchidos em "Montar Orçamento", eles aparecerão aqui automaticamente.
              </div>
            ) : (
              <>
                {/* Filters */}
                <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                    <Filter className="w-4 h-4" />
                    Filtros ({filtered.length} de {pricedContracts.length} eventos precificados)
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Tema</label>
                      <select
                        value={themeFilter}
                        onChange={(e) => setThemeFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      >
                        <option value="todos">Todos os temas</option>
                        {themes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Convidados (mín.)</label>
                      <input
                        type="number"
                        value={minGuests}
                        onChange={(e) => setMinGuests(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Ex: 20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Convidados (máx.)</label>
                      <input
                        type="number"
                        value={maxGuests}
                        onChange={(e) => setMaxGuests(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Ex: 80"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={onlyWithComponents}
                          onChange={(e) => setOnlyWithComponents(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                        Só com itens detalhados
                      </label>
                    </div>
                    <div className="flex items-end justify-end">
                      <button
                        onClick={exportSuggestedTable}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Exportar tabela sugerida
                      </button>
                    </div>
                  </div>
                </section>

                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    label="Eventos analisados"
                    value={String(summary.count)}
                    icon={<ListChecks className="w-4 h-4" />}
                  />
                  <StatCard
                    label="Valor médio"
                    value={formatCurrency(summary.avgTotal)}
                    hint={`Mediana: ${formatCurrency(summary.medianTotal)}`}
                    icon={<DollarSign className="w-4 h-4" />}
                  />
                  <StatCard
                    label="Faixa de valores"
                    value={`${formatCurrency(summary.minTotal)} – ${formatCurrency(summary.maxTotal)}`}
                    icon={<TrendingUp className="w-4 h-4" />}
                  />
                  <StatCard
                    label="Preço médio por convidado"
                    value={formatCurrency(summary.avgPricePerGuest)}
                    hint={`Média de ${summary.avgGuests.toFixed(0)} convidados/evento`}
                    icon={<Users className="w-4 h-4" />}
                  />
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                    <div className="flex items-center gap-2 font-semibold text-slate-800 mb-4">
                      <BarChart3 className="w-4 h-4 text-teal-600" />
                      Valor médio por tema (top 8)
                    </div>
                    <MiniBarChart data={themeChartData} />
                  </section>
                  <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                    <div className="flex items-center gap-2 font-semibold text-slate-800 mb-4">
                      <Users className="w-4 h-4 text-teal-600" />
                      Valor médio por faixa de convidados
                    </div>
                    <MiniBarChart data={bracketChartData} />
                  </section>
                </div>

                {/* Suggested price table sections */}
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 pt-2">
                  <Sparkles className="w-4 h-4 text-teal-600" />
                  Sugestão de tabela de preços (calculada a partir do histórico real)
                </div>

                <StatTable
                  title="Itens do orçamento"
                  icon={<DollarSign className="w-4 h-4 text-teal-600" />}
                  rows={componentRows}
                />

                <StatTable
                  title="Extras e campos personalizados"
                  icon={<Tag className="w-4 h-4 text-teal-600" />}
                  rows={customFieldRows}
                />

                <StatTable
                  title="Personagens (frequência e preço estimado por personagem)"
                  icon={<Sparkles className="w-4 h-4 text-teal-600" />}
                  rows={characterRows}
                />

                <StatTable
                  title="Valor total por tema de festa"
                  icon={<BarChart3 className="w-4 h-4 text-teal-600" />}
                  rows={themeRows}
                />

                <StatTable
                  title="Valor total por faixa de convidados"
                  icon={<Users className="w-4 h-4 text-teal-600" />}
                  rows={guestBracketRows}
                />

                {/* Raw data table */}
                <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div className="font-semibold text-slate-800">Eventos precificados (dados brutos)</div>
                    <button
                      onClick={exportRawData}
                      className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Exportar CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-[480px]">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                        <tr>
                          <th className="text-left px-5 py-2 font-semibold cursor-pointer select-none" onClick={() => toggleSort('data')}>
                            Data {sortKey === 'data' && (sortDir === 'asc' ? '↑' : '↓')}
                          </th>
                          <th className="text-left px-3 py-2 font-semibold">Contratante</th>
                          <th className="text-left px-3 py-2 font-semibold">Tema</th>
                          <th className="text-right px-3 py-2 font-semibold cursor-pointer select-none" onClick={() => toggleSort('convidados')}>
                            Convidados {sortKey === 'convidados' && (sortDir === 'asc' ? '↑' : '↓')}
                          </th>
                          <th className="text-right px-5 py-2 font-semibold cursor-pointer select-none" onClick={() => toggleSort('valor')}>
                            Valor {sortKey === 'valor' && (sortDir === 'asc' ? '↑' : '↓')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortedTable.map(({ contract, total }) => (
                          <tr key={contract.id} className="hover:bg-slate-50">
                            <td className="px-5 py-2 text-slate-600">{contract.dataFesta ? formatDate(contract.dataFesta) : '-'}</td>
                            <td className="px-3 py-2 text-slate-700">{contract.contratante || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{contract.temaFesta || '-'}</td>
                            <td className="px-3 py-2 text-right text-slate-600">{contract.numeroConvidados || '-'}</td>
                            <td className="px-5 py-2 text-right font-semibold text-slate-800">{formatCurrency(total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default BudgetInsights;
